import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ROLE_DESCRIPTIONS = {
  super_admin:          'Full platform access — no restrictions.',
  system_administrator: 'Manage system settings, admin users, and configurations.',
  customer_support:     'View and manage customer accounts and tickets.',
  credit_analyst:       'Analyse loan applications and pull credit bureau reports.',
  risk_officer:         'Assess risk, approve risk exceptions, and override scores.',
  operations_officer:   'Process loan lifecycle operations and disbursements.',
  finance_officer:      'Manage disbursements, refunds, and financial reports.',
  compliance_officer:   'Monitor KYC, AML flags, and regulatory compliance.',
  auditor:              'Read-only access to all audit logs and reports.',
  api_administrator:    'Generate and manage API keys and client integrations.',
};

const MODULE_COLORS = {
  customers: '#5B8EFF', loans: '#00C896', disbursements: '#F5A623',
  refunds: '#FF4757', kyc: '#818CF8', fraud: '#ff9900',
  audit: '#94a3b8', api: '#20E8AC', system: '#60a5fa', reports: '#a78bfa', credit: '#fb923c',
};

export default function RoleManagement() {
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => adminService.getRoles().then(r => r.data.data),
  });

  const roles = rolesData?.results || rolesData || [];

  return (
    <div>
      <div className="page-header">
        <h1>Role Management</h1>
        <p>RBAC roles and their permissions — assign via user management</p>
      </div>

      <div className="alert alert-info" style={{ maxWidth: 720, marginBottom: '1.5rem' }}>
        <span>ℹ</span>
        <span>Role assignments for high-privilege roles (Super Admin, System Administrator, Finance Officer) automatically create a checker approval request before taking effect.</span>
      </div>

      {rolesLoading ? <LoadingSpinner /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1rem' }}>
          {roles.map(role => (
            <div key={role.id} className="card" style={{ position: 'relative', overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: 'linear-gradient(90deg, var(--primary), var(--violet))',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-1)' }}>
                    {role.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>
                    {ROLE_DESCRIPTIONS[role.name] || ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--primary)' }}>{role.user_count}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase' }}>Users</div>
                </div>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                  Permissions ({role.permissions.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {role.permissions.length === 0 ? (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>No permissions assigned</span>
                  ) : role.permissions.map(code => {
                    const [mod] = code.split('.');
                    const color = MODULE_COLORS[mod] || '#94a3b8';
                    return (
                      <span key={code} style={{
                        fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.5rem',
                        borderRadius: 99, background: `${color}15`, color, border: `1px solid ${color}25`,
                        fontFamily: 'monospace',
                      }}>{code}</span>
                    );
                  })}
                </div>
              </div>

              {!role.is_active && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#FF4757', fontWeight: 700 }}>INACTIVE</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
